import { createFileRoute, useNavigate } from "@tanstack/react-router";

import { catalogItemsSearchSchema, type CatalogItemsSearch } from "@/api/contracts/catalog";
import { catalogSearchNavigation } from "@/lib/catalog-list-url-state";
import { ItemCatalogPage } from "@/components/catalog/item-catalog-page";
import { requireCatalogAccess } from "@/lib/route-guards";

// The Item Catalog list. One route for the whole surface: page, size, sort, and
// view are search params rather than a subtree, which is both the old stack's
// URL shape and what makes every state of the list deep-linkable and
// back-button-safe.
//
// Lives in a directory (rather than as a flat `items.tsx`) so the details and
// edit slices can add `$itemId` siblings without this file becoming their
// layout route — the same shape `specifications/` has.
//
// The `_authed` layout has already guaranteed a session; this gate narrows that
// to a session that may browse the catalog. It is a door, not the enforcement —
// Python use cases remain that — so nothing behind it may rely on having passed
// here, and the server function checks the same capability itself.

export const Route = createFileRoute("/_authed/_chrome/items/")({
  // Zod v4 implements Standard Schema, so the schema passes in directly. Every
  // field inside it drops to `undefined` rather than failing, so a hand-edited
  // or stale URL never breaks the navigation.
  validateSearch: catalogItemsSearchSchema,
  beforeLoad: ({ context }) => {
    requireCatalogAccess(context.currentUser);
  },
  head: () => ({ meta: [{ title: "Item Catalog" }] }),
  component: ItemCatalogRoute,
});

function ItemCatalogRoute() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  return (
    <ItemCatalogPage
      search={search}
      // The merge and the push-vs-replace rule both live in
      // `catalogSearchNavigation`, so the tests that exercise this route's URL
      // behaviour run the route's own write rather than a copy of it.
      onSearchChange={(next, options) => {
        void navigate(catalogSearchNavigation<CatalogItemsSearch>(next, options));
      }}
    />
  );
}
